/**
 * IncomingHandoffBanner — shown at the top of the caregiver's shift report
 * when a colleague has voice-recorded a handoff for this client in the last
 * 36h that hasn't been acked yet.
 *
 *   <IncomingHandoffBanner clientId={clientId} />
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Sparkles, AlertTriangle, CheckCircle2, ListChecks, Loader2, Play, Pause } from "lucide-react";

const MOOD_COLOR = {
  great: "bg-emerald-100 text-emerald-800",
  okay: "bg-amber-100 text-amber-900",
  rough: "bg-rose-100 text-rose-800",
};

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.round(ms / 60000);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.round(hr / 24)}d ago`;
  } catch { return ""; }
}

export default function IncomingHandoffBanner({ clientId }) {
  const [handoff, setHandoff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(false);
  const [acked, setAcked] = useState(false);
  const [audioState, setAudioState] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);

  useEffect(() => () => {
    // Cleanup audio on unmount
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!clientId) return;
    let alive = true;
    api.get(`/clients/${clientId}/handoffs/latest`)
      .then((res) => { if (alive) setHandoff(res.data || null); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [clientId]);

  const ack = async () => {
    if (!handoff) return;
    setAcking(true);
    try {
      await api.post(`/handoffs/${handoff.id}/ack`);
      // Belt + suspenders: clear both flags so the banner unmounts deterministically,
      // even if a downstream re-render or stub keeps returning the handoff.
      setAcked(true);
      setHandoff(null);
    } finally {
      setAcking(false);
    }
  };

  const toggleAudio = async () => {
    if (!handoff) return;
    // Pause if already playing
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setAudioState("idle");
      return;
    }
    // Resume if already loaded
    if (audioRef.current && audioRef.current.src) {
      try {
        await audioRef.current.play();
        setAudioState("playing");
      } catch { setAudioState("idle"); }
      return;
    }
    // First play — fetch the MP3 from the backend
    setAudioState("loading");
    try {
      const res = await api.get(`/handoffs/${handoff.id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      const cleanup = () => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
      };
      a.addEventListener("ended", () => { setAudioState("idle"); cleanup(); });
      a.addEventListener("pause", () => setAudioState((s) => (s === "playing" ? "idle" : s)));
      audioRef.current = a;
      await a.play();
      setAudioState("playing");
    } catch (e) {
      setAudioState("idle");
    }
  };

  if (loading) return null;
  if (!handoff) return null;
  if (acked) return null;

  return (
    <div
      className="rounded-2xl border border-cyan-300 bg-gradient-to-br from-cyan-50 to-white p-4 shadow-sm"
      data-testid="incoming-handoff-banner"
    >
      <div className="flex items-start gap-2.5 mb-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-cyan-600 text-white">
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-sm font-semibold text-clinical-text">
            Handoff from {handoff.outgoing_caregiver_name?.split(" ")[0] || "the last caregiver"}
          </div>
          <div className="text-[11px] text-clinical-subtext">
            {relativeTime(handoff.sent_at)} · for {handoff.client_name}
          </div>
        </div>
        {handoff.mood && (
          <span className={`pill text-[11px] ${MOOD_COLOR[handoff.mood] || "bg-clinical-bg"}`}
                data-testid="incoming-handoff-mood">
            {handoff.mood}
          </span>
        )}
      </div>

      {handoff.summary && (
        <p className="text-sm text-clinical-text mb-2 leading-relaxed">{handoff.summary}</p>
      )}

      {(handoff.key_points || []).length > 0 && (
        <div className="mb-2">
          <div className="label-eyebrow !text-cyan-700 mb-1">Key points</div>
          <ul className="list-disc pl-5 text-sm text-clinical-text space-y-0.5">
            {handoff.key_points.map((p, i) => (<li key={i}>{p}</li>))}
          </ul>
        </div>
      )}

      {(handoff.concerns || []).length > 0 && (
        <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
          <div className="label-eyebrow !text-amber-800 mb-1 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Watch for
          </div>
          <ul className="list-disc pl-5 text-sm text-amber-900 space-y-0.5">
            {handoff.concerns.map((c, i) => (<li key={i}>{c}</li>))}
          </ul>
        </div>
      )}

      {(handoff.todo || []).length > 0 && (
        <div className="mb-2">
          <div className="label-eyebrow text-clinical-accent mb-1 flex items-center gap-1">
            <ListChecks className="h-3 w-3" /> To do this shift
          </div>
          <ul className="list-disc pl-5 text-sm text-clinical-text space-y-0.5">
            {handoff.todo.map((t, i) => (<li key={i}>{t}</li>))}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1.5">
        <button
          type="button"
          onClick={toggleAudio}
          disabled={audioState === "loading"}
          className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-300 bg-white px-3 py-1.5 text-xs font-semibold text-cyan-700 hover:bg-cyan-50 disabled:opacity-50"
          data-testid="incoming-handoff-listen"
        >
          {audioState === "loading" ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : audioState === "playing" ? (
            <Pause className="h-3 w-3" />
          ) : (
            <Play className="h-3 w-3 fill-current" />
          )}
          {audioState === "playing" ? "Pause" : audioState === "loading" ? "Loading…" : "Listen"}
        </button>
        <button
          type="button"
          onClick={ack}
          disabled={acking}
          className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
          data-testid="incoming-handoff-ack"
        >
          {acking ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
          Got it
        </button>
      </div>
    </div>
  );
}
